import React, { FC, ReactNode, useState } from 'react';

import { Collapse, Container, Typography } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';

import ChevronTopIcon from '~/assets/icons/ChevronTopIcon';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import PriceSwitcherSticky from '~/pages/PricePage/PriceSwitcherSticky';
import {
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
  useIsWidthUp,
} from '~/theme';

interface StickyBlockProps {
  visible: boolean;
  formattedPrice?: [string | undefined, string, string];
  isAnnual: boolean;
  hasDurationChoices: boolean;
  toggleSwitch: () => void;
  button: ReactNode;
  subtitle: string;
  title: string;
  vatTag: string;
}

const StickyBlock: FC<StickyBlockProps> = ({
  visible,
  formattedPrice = [],
  isAnnual,
  hasDurationChoices,
  toggleSwitch,
  button,
  subtitle,
  title,
  vatTag,
}) => {
  const isMobile = !useIsWidthUp('lg');
  const [currencySymbol, wholePrice, decimalPrice] = formattedPrice;
  const [isOpened, setIsOpened] = useState(false);
  const { t } = useTenantTranslation();

  const nonZeroDecimal = decimalPrice === '00' ? '' : decimalPrice;

  const content = (
    <StickyContainer>
      <StickyInfo>
        <StickyInfoTitle variant="h4">{title}</StickyInfoTitle>
        <StickyInfoSubtitle>{subtitle}</StickyInfoSubtitle>
      </StickyInfo>
      <StickySwitcher>
        {wholePrice ? (
          <CardPrice>
            <CardCurrency>{currencySymbol}</CardCurrency>
            <CardPriceValue>{wholePrice}</CardPriceValue>
            {nonZeroDecimal && <PriceValueDecimal>.{decimalPrice}</PriceValueDecimal>}
            <VatTag>
              {t(isAnnual ? 'page.price.annually' : 'page.price.monthly')} {vatTag}
            </VatTag>
          </CardPrice>
        ) : (
          <PricePlaceholder>{t('page.price.enterpriseHeader')}</PricePlaceholder>
        )}
        {wholePrice && hasDurationChoices && (
          <PriceSwitcherSticky isAnnual={isAnnual} toggleSwitch={toggleSwitch} />
        )}
      </StickySwitcher>
      {button}
    </StickyContainer>
  );

  return isMobile ? (
    <StickyBlockWrapper>
      <StyledStickyBlock visible={visible}>
        <CollapseHeader onClick={() => setIsOpened((old) => !old)}>
          {title}
          <StyledChevron reversed={isOpened} />
        </CollapseHeader>
        <Collapse in={isOpened}>{content}</Collapse>
      </StyledStickyBlock>
    </StickyBlockWrapper>
  ) : (
    <StickyBlockWrapper>
      <StyledStickyBlock visible={visible}>{content}</StyledStickyBlock>
    </StickyBlockWrapper>
  );
};

export default StickyBlock;

const StyledChevron = styled(ChevronTopIcon)`
  position: relative;
  top: -3px;
  width: 18px;
  min-width: 18px;
  margin-left: auto;
  transform: rotate(${({ reversed }) => (reversed ? -180 : 0)}deg);
  color: ${({ theme }) => theme.palette.common.violet};
  transition: transform 0.3s;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    top: 0;
  }
`;
const StickyContainer = styled(Container)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  height: 100%;
  padding-bottom: ${SUBMIDDLE_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex-wrap: nowrap;
    padding-bottom: 0;
  }
`;
const StickyInfo = styled('div')`
  width: 100%;
  margin-right: auto;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    width: unset;
    padding-right: ${TINY_MARGIN_PX};
  }
`;
const StickyInfoTitle = styled(Typography)`
  display: none;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    display: block;
  }
`;
const StickyInfoSubtitle = styled('span')`
  display: block;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
  padding-bottom: ${SMALL_MARGIN_PX};
  border-bottom: 1px solid ${({ theme }) => alpha(theme.palette.common.white, 0.33)};
  color: ${({ theme }) => theme.palette.common.gray};
  font-size: 14px;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    display: inline;
    padding-bottom: 0;
    margin-bottom: 0;
    color: ${({ theme }) => theme.palette.common.white};
    border-bottom: none;
  }
`;
const StickySwitcher = styled('div')`
  display: flex;
  flex-direction: column;
  margin-right: auto;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    margin-right: ${MIDDLE_MARGIN_PX};
  }
`;
const CardPrice = styled(Typography)`
  position: relative;
  top: -5px;
  display: flex;
  align-self: center;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${TINY_MARGIN_PX};
  }
  ${({ theme }) => theme.breakpoints.up('xl')} {
    top: 0;
    align-self: flex-start;
  }
`;
const CardCurrency = styled('span')`
  margin-right: ${TINY_MARGIN_PX};
  font-size: 24px;
  font-weight: 700;
  line-height: 24px;
  align-self: flex-end;
`;
const CardPriceValue = styled('span')`
  font-size: 56px;
  font-weight: 700;
  line-height: 46px;
  text-align: center;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    font-size: 48px;
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    text-align: left;
    font-size: 64px;
    line-height: 60px;
  }
`;
const PriceValueDecimal = styled(CardPriceValue)`
  && {
    font-size: 32px;
    line-height: 28px;
    align-self: flex-end;
    ${({ theme }) => theme.breakpoints.up('lg')} {
      font-size: 36px;
      line-height: 32px;
    }
  }
`;
const VatTag = styled('span')`
  margin-left: ${TINY_MARGIN_PX};
  align-self: flex-end;
  max-width: 130px;
  font-size: 14px;
  color: ${({ theme }) => alpha(theme.palette.common.white, 0.33)};
`;
const PricePlaceholder = styled('span')`
  position: relative;
  top: -2px;
  font-size: 48px;
  font-weight: 700;
  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 54px;
  }
`;
const StickyBlockWrapper = styled('div')`
  position: relative;
  min-height: 64px;
  ${({ theme }) => theme.breakpoints.up('md')} {
    min-height: 120px;
    height: 0;
  }
`;
const StyledStickyBlock = styled('div', {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  position: fixed;
  z-index: 1500;
  min-height: 64px;
  width: 100%;
  bottom: ${({ visible }) => (visible ? '0px' : '-250px')};
  background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  transition: 0.3s;
  border-top-left-radius: ${MIDDLE_RADIUS_PX};
  border-top-right-radius: ${MIDDLE_RADIUS_PX};
  ${({ theme }) => theme.breakpoints.up('lg')} {
    min-height: 120px;
    height: 0;
    bottom: ${({ visible }) => (visible ? '0px' : '-120px')};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;
const CollapseHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  padding: ${SMALL_MARGIN_PX} ${SMALL_MARGIN_PX} ${TINY_MARGIN_PX};
  ${({ theme }) => theme.breakpoints.up('md')} {
    height: 64px;
  }
`;
